import { create } from 'zustand';
import { get as idbGet, set as idbSet, createStore as createIdbStore } from 'idb-keyval';
import type { CreateOrderRequest, CreateOrderResponse, ReceiptData } from '@matix/types';
import { orderAPI, getApiErrorMessage } from '@matix/api-client';
import { isNetworkError, useNetwork } from '../lib/netStatus';

/**
 * Sales taken while the till had no network, waiting to be posted.
 *
 * The whole feature rests on one property that already exists server-side:
 * `createCashOrder` looks the idempotency key up against
 * `Inv_Receipt_h.UniqueKey`, which carries a unique index, BEFORE it writes
 * anything. That check is durable — not the 24-hour Redis one — so a sale
 * queued for three days and sent twice still books exactly once. Retrying is
 * therefore always safe, and this store never has to reason about whether a
 * request "might have got through".
 *
 * Two rules keep it honest:
 *
 *   1. The idempotency key is generated once, when the money is taken, and is
 *      never regenerated. It IS the entry's id. Regenerating it on retry would
 *      turn one sale into two receipts.
 *   2. The request is stored exactly as the normal charge path built it. This
 *      store never re-derives a total — the round-then-sum arithmetic the cart
 *      and the server agree on lives in one place and this is not it.
 */

export type QueuedStatus = 'queued' | 'sending' | 'synced' | 'failed';

export interface QueuedSale {
  /** = request.idempotency_key. Generated once, reused on every attempt. */
  id: string;
  status: QueuedStatus;
  attempts: number;
  /** When the customer actually paid — the receipt's own timestamp. */
  capturedAt: string;
  /** What was printed on the customer's copy, e.g. `OFF-12-3`. */
  provisionalNo: string;
  /** Exactly what will be POSTed. */
  request: CreateOrderRequest;
  /** Exactly what was printed, so the receipt can be re-printed from the queue. */
  receipt: ReceiptData | null;
  /** Cashier-facing summary, so the sync screen needs nothing else. */
  summary: { total: number; itemCount: number; customerLabel: string | null };
  lastError?: { message: string; code?: string | null } | null;
  /** Populated once the server has it. */
  result?: { orderId: number; entryNo: number } | null;
  syncedAt?: string | null;
}

const IDB_STORE = createIdbStore('matix-pos-offline', 'outbox');
const KEY = 'sales';
/** Synced rows are kept this long so a cashier can still reconcile them. */
const KEEP_SYNCED_MS = 7 * 24 * 60 * 60 * 1000;

async function persist(entries: QueuedSale[]): Promise<void> {
  try {
    await idbSet(KEY, entries, IDB_STORE);
  } catch {
    // A full or blocked IndexedDB must not take the till down mid-sale. The
    // entry stays in memory and the cashier still sees it in the queue; it is
    // only the survive-a-reload guarantee that is lost, and the header badge
    // is what stops that going unnoticed.
  }
}

interface OutboxState {
  entries: QueuedSale[];
  /** False until IndexedDB has been read — nothing should sync before then. */
  loaded: boolean;
  syncing: boolean;

  hydrate: () => Promise<void>;
  enqueue: (entry: Omit<QueuedSale, 'status' | 'attempts'>) => Promise<void>;
  /** Post everything queued, oldest first. Safe to call at any time. */
  sync: () => Promise<{ synced: number; failed: number }>;
  /** Put one failed sale back in the queue for another attempt. */
  requeue: (id: string) => Promise<void>;
  /** How many sales are still owed to the server. */
  pendingCount: () => number;
  failedCount: () => number;
}

export const useOutbox = create<OutboxState>((set, get) => ({
  entries: [],
  loaded: false,
  syncing: false,

  hydrate: async () => {
    if (get().loaded) return;
    let stored: QueuedSale[] = [];
    try {
      stored = (await idbGet<QueuedSale[]>(KEY, IDB_STORE)) ?? [];
    } catch {
      stored = [];
    }
    // Anything caught mid-flight by a reload is queued again, not lost. The
    // server's idempotency check makes a double send harmless.
    const now = Date.now();
    const entries = stored
      .map((e) => (e.status === 'sending' ? { ...e, status: 'queued' as const } : e))
      .filter(
        (e) =>
          e.status !== 'synced' ||
          !e.syncedAt ||
          now - new Date(e.syncedAt).getTime() < KEEP_SYNCED_MS,
      );
    set({ entries, loaded: true });
    if (entries.some((e) => e.status === 'queued')) void get().sync();
  },

  enqueue: async (entry) => {
    const next: QueuedSale = { ...entry, status: 'queued', attempts: 0 };
    const entries = [...get().entries, next];
    set({ entries });
    await persist(entries);
  },

  sync: async () => {
    if (get().syncing || !get().loaded) return { synced: 0, failed: 0 };
    set({ syncing: true });
    let synced = 0;
    let failed = 0;

    try {
      // Oldest first, one at a time. Invoice numbers are allocated at write
      // time, so posting in capture order is what keeps them in the order the
      // money was actually taken.
      for (;;) {
        const nextUp = [...get().entries]
          .filter((e) => e.status === 'queued')
          .sort((a, b) => a.capturedAt.localeCompare(b.capturedAt))[0];
        if (!nextUp) break;

        patch(set, get, nextUp.id, { status: 'sending' });

        try {
          const res: CreateOrderResponse = await orderAPI.create(nextUp.request);
          useNetwork.getState().setOnline(true);
          await patch(set, get, nextUp.id, {
            status: 'synced',
            result: { orderId: res.order_id, entryNo: res.entry_no },
            syncedAt: new Date().toISOString(),
            lastError: null,
          });
          synced += 1;
        } catch (err) {
          const attempts = nextUp.attempts + 1;
          if (isNetworkError(err)) {
            // Still offline. Put it back and stop — the rest of the queue is
            // behind the same dead network, and hammering it wakes nothing up.
            useNetwork.getState().setOnline(false);
            await patch(set, get, nextUp.id, {
              status: 'queued',
              attempts,
              lastError: { message: 'No connection' },
            });
            break;
          }
          // The server answered and refused. Retrying an unchanged payload
          // will be refused identically, so it stops being queued and becomes
          // something a human has to look at — but it must not block the sales
          // behind it, which are probably fine.
          await patch(set, get, nextUp.id, {
            status: 'failed',
            attempts,
            lastError: {
              message: getApiErrorMessage(err) || 'The server rejected this sale',
              code: readErrorCode(err),
            },
          });
          failed += 1;
        }
      }
    } finally {
      set({ syncing: false });
    }

    return { synced, failed };
  },

  requeue: async (id) => {
    await patch(set, get, id, { status: 'queued', lastError: null });
    void get().sync();
  },

  pendingCount: () =>
    get().entries.filter((e) => e.status === 'queued' || e.status === 'sending').length,

  failedCount: () => get().entries.filter((e) => e.status === 'failed').length,
}));

/** Update one entry in place and write the whole queue back to IndexedDB. */
async function patch(
  set: (partial: Partial<OutboxState>) => void,
  get: () => OutboxState,
  id: string,
  changes: Partial<QueuedSale>,
): Promise<void> {
  const entries = get().entries.map((e) => (e.id === id ? { ...e, ...changes } : e));
  set({ entries });
  await persist(entries);
}

function readErrorCode(err: unknown): string | null {
  const e = err as { response?: { data?: { code?: string; error?: string } } };
  return e?.response?.data?.code ?? null;
}

/**
 * The number printed on an offline receipt, in place of the invoice number the
 * server has not issued yet.
 *
 * Deliberately not a bare integer: it must be impossible to mistake for a real
 * invoice number, both on the paper in the customer's hand and in a support
 * conversation. The counter is per device and survives reloads.
 */
export function nextProvisionalNo(drawerId: number | null): string {
  const KEY_SEQ = 'pos:cashier:offline-seq';
  let n = 1;
  try {
    n = Number(window.localStorage.getItem(KEY_SEQ) ?? '0') + 1;
    window.localStorage.setItem(KEY_SEQ, String(n));
  } catch {
    n = Math.floor(Math.random() * 100000);
  }
  return `OFF-${drawerId ?? 0}-${n}`;
}
